/**
 * User Utility
 * 
 * Provides reusable methods for user-related data processing.
 */
export class UserUtil {
  /**
   * Formats a user's full name by joining first and last name, 
   * handling null, undefined, and literal "null"/"undefined" strings.
   * 
   * @param firstName - The user's first name
   * @param lastName - The user's last name
   * @returns Formatted full name string
   */
  static formatFullName(firstName: any, lastName: any): string {
    return [firstName, lastName]
      .filter(name => this.sanitize(name))
      .join(' ')
      .trim();
  }

  /**
   * Sanitizes a string value, returning empty string if it's "null", "undefined", or falsy.
   * Also strips local drive images from HTML contents (e.g., MS Word copy-paste file:/// paths).
   * 
   * @param value - The value to sanitize
   * @returns Sanitized string
   */
  static sanitize(value: any): string {
    if (!value) return '';
    const n = String(value).toLowerCase();
    if (n === 'null' || n === 'undefined') return '';
    
    let strVal = String(value).trim();
    if (strVal.includes('<img') || strVal.includes('<v:imagedata')) {
      strVal = this.sanitizeLocalImages(strVal);
    }
    
    return strVal;
  }

  /**
   * Strips out any local file image references (e.g., file:///, local drive paths, AppData Temp paths)
   * from an HTML string to prevent security warnings, local filesystem errors, or rendering crashes.
   * 
   * @param html - The HTML string to sanitize
   * @returns Cleaned HTML string
   */
  static sanitizeLocalImages(html: string): string {
    if (!html) return '';
    // Strips <img> tags containing local references in double or single quotes
    let cleaned = html.replace(/<img[^>]+src=["'](file:\/\/[^"']+|[a-zA-Z]:\\[^"']+|[a-zA-Z]:\/[^"']+|[^"']*(?:msohtmlclip|appdata\/local\/temp|steve~|stella~)[^"']+)["'][^>]*>/gi, '');
    
    // Strips <v:imagedata> tags referencing local files
    cleaned = cleaned.replace(/<v:imagedata[^>]+src=["'](file:\/\/[^"']+|[a-zA-Z]:\\[^"']+|[a-zA-Z]:\/[^"']+|[^"']*(?:msohtmlclip|appdata\/local\/temp|steve~|stella~)[^"']+)["'][^>]*>/gi, '');
    
    return cleaned;
  }

  /**
   * Gets initials from a user's name (useful for avatars)
   * 
   * @param firstName - The user's first name
   * @param lastName - The user's last name
   * @returns Initials string
   */
  static getInitials(firstName: any, lastName: any): string {
    const fullName = this.formatFullName(firstName, lastName);
    if (!fullName) return '';
    
    return fullName
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  }

  /**
   * Deeply sanitizes an object or array, replacing all "null"/"undefined" strings with empty strings.
   * 
   * @param data - The object or array to sanitize
   * @returns Sanitized data
   */
  static deepSanitize<T>(data: T): T {
    if (!data) return data;

    if (Array.isArray(data)) {
      return data.map(item => this.deepSanitize(item)) as unknown as T;
    }

    if (typeof data === 'object' && data !== null) {
      const sanitizedObj: any = {};
      Object.keys(data).forEach(key => {
        const val = (data as any)[key];
        if (typeof val === 'string') {
          sanitizedObj[key] = this.sanitize(val);
        } else if (typeof val === 'object' && val !== null) {
          sanitizedObj[key] = this.deepSanitize(val);
        } else {
          sanitizedObj[key] = val;
        }
      });
      return sanitizedObj as T;
    }

    return data;
  }
}
